import { config } from './config.js';
import { getToken, getUser, isConfigured } from './auth.js';
import { captureError } from './sentry.js';

async function authedFetch(path, opts = {}) {
  if (!isConfigured()) throw new Error('apiClient: Clerk not configured');
  const token = await getToken();
  if (!token) throw new Error('apiClient: not signed in');
  const res = await fetch(`${config.apiUrl}${path}`, {
    ...opts,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Sayermore-API-Version': config.apiVersion,
      'X-Sayermore-Client-Version': config.clientVersion,
      ...opts.headers,
    },
  });
  if (!res.ok) {
    const err = new Error(`API ${res.status} ${path}`);
    err.status = res.status;
    captureError(err, { path, status: res.status });
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

function signedIn() {
  return Boolean(getUser());
}

export const apiClient = {
  get enabled() {
    return isConfigured() && signedIn();
  },

  async listFolders() {
    if (!this.enabled) return [];
    try {
      const result = await authedFetch('/v1/folders');
      return Array.isArray(result) ? result : [];
    } catch {
      return [];
    }
  },

  async listDocsInFolder(folderKey) {
    if (!this.enabled) return { folder: null, docs: [] };
    try {
      return await authedFetch(`/v1/folders/${encodeURIComponent(folderKey)}/docs`);
    } catch {
      return { folder: null, docs: [] };
    }
  },

  async listSessions() {
    if (!this.enabled) return [];
    try {
      const result = await authedFetch('/v1/sessions');
      return Array.isArray(result) ? result : [];
    } catch {
      return [];
    }
  },

  async getDoc(folderKey, docKey) {
    if (!this.enabled) return null;
    try {
      return await authedFetch(`/v1/folders/${encodeURIComponent(folderKey)}/docs/${encodeURIComponent(docKey)}`);
    } catch {
      return null;
    }
  },

  async pushSession(session) {
    if (!this.enabled) return null;
    return authedFetch('/v1/sessions', { method: 'POST', body: JSON.stringify(session) });
  },
};

// Keep the global for legacy inline handlers that still reference it.
if (typeof window !== 'undefined') window.apiClient = apiClient;
